import {
  useModalContext,
  WSButton,
  WSElementProps,
  WSMessageBox
} from "@wingspanhq/fe-component-library";
import { VerificationStatus } from "@wingspanhq/payments/dist/interfaces";
import React from "react";
import { useHistory } from "react-router-dom";
import { useWSMutation } from "../../../query/helpers";
import { useUserId } from "../../../query/hooks/helpers";
import { usePaymentsStatusQuery } from "../../../query/payments/queries";
import { QUERY_USERS_ACTIVITIES } from "../../../query/users/keys";
import { useMemberProfile } from "../../../query/users/queries";
import { getIsDomesticMember } from "../../../query/users/selectors";
import { WSQueries } from "../../../query/WSQuery";
import { paymentsService } from "../../../services/payments";
import { usersService } from "../../../services/users";
import { openIntercom } from "../../../utils/intercom";
import { windowOpen } from "../../../utils/windowOpen";
import {
  TINVerificationModal,
  TIN_VERIFICATION_MODAL
} from "../TINVerificationModal/TINVerificationModal";

type Props = WSElementProps;

export const PaymentsStatusBanner: React.FC<Props> = ({ ...elementProps }) => {
  const userId = useUserId();
  const paymentsStatusQuery = usePaymentsStatusQuery();
  const memberQuery = useMemberProfile(userId);
  const history = useHistory();
  const { openModal } = useModalContext();

  const [startSetupOver] = useWSMutation(
    async () => {
      history.push("/member/invoices/set-up");
      await usersService.activity.update(userId, {
        flows: {
          paymentsSetup: {
            complete: false,
            version: 3,
            currentStep: 0,
            totalSteps: 5
          }
        }
      });
    },
    {
      dependencies: [QUERY_USERS_ACTIVITIES]
    }
  );

  const handleUploadDocuments = async () => {
    try {
      const {
        applicationLink
      } = await paymentsService.service.banking.application.get(userId);
      windowOpen(applicationLink);
    } catch (error) {
      console.error(error);
      openIntercom();
    }
  };

  return (
    <>
      <WSQueries queries={{ paymentsStatusQuery, memberQuery }}>
        {({
          paymentsStatusQuery: { data: paymentsStatus },
          memberQuery: { data: member }
        }) => {
          const isDomesticMember = getIsDomesticMember(member);
          return isDomesticMember &&
            paymentsStatus.verifications.taxDocumentation ===
              VerificationStatus.None ? (
            <WSMessageBox.Error
              size="Large"
              icon="warning"
              noBorder
              {...elementProps}
            >
              To use Wingspan payments we'll need to collect some information,{" "}
              <WSButton.Link inline onClick={startSetupOver}>
                click here to get started
              </WSButton.Link>
              .
            </WSMessageBox.Error>
          ) : isDomesticMember &&
            paymentsStatus.verifications.taxDocumentation ===
              VerificationStatus.Pending ? (
            <WSMessageBox.Warning icon="clock" noBorder {...elementProps}>
              <b>Digital payments pending.</b> It can take up to 48 hours to
              enable digital payments.{" "}
              <WSButton.Link inline onClick={openIntercom}>
                Learn more
              </WSButton.Link>
            </WSMessageBox.Warning>
          ) : isDomesticMember &&
            paymentsStatus.verifications.taxDocumentation ===
              VerificationStatus.Failed ? (
            <WSMessageBox.Error icon="clock" noBorder {...elementProps}>
              We were not able to verify your tax identification information,{" "}
              <WSButton.Link
                inline
                onClick={() => {
                  openModal(TIN_VERIFICATION_MODAL);
                }}
              >
                click here
              </WSButton.Link>{" "}
              to verify and update your information.
            </WSMessageBox.Error>
          ) : (isDomesticMember &&
              paymentsStatus.verifications.banking ===
                VerificationStatus.Failed) ||
            paymentsStatus.verifications.cardPayments ===
              VerificationStatus.Failed ? (
            <WSMessageBox.Regular noBorder {...elementProps}>
              <b>You're all set to receive payments!</b> We need some additional
              information to enable all Wingspan features.{" "}
              <WSButton.Link inline onClick={handleUploadDocuments}>
                Click here to upload required documents
              </WSButton.Link>
              .
            </WSMessageBox.Regular>
          ) : null;
        }}
      </WSQueries>
      <TINVerificationModal />
    </>
  );
};
